import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErrorApi } from '../api/cliente';
import type {
  Opcion, Orden, Pagina, Tot, PorRequerir, GrupoPorRequerir,
} from '../api/tipos';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';
import { Cargando, Dialogo, DialogoMotivo, Error, Paginacion } from '../componentes/comunes';

/**
 * La ficha de la orden de trabajo, para abrirla desde el folio del vale.
 *
 * Karin: «puedes habilitar que desde una OT al clic en la OT se vaya a
 * esa orden?». Es lo que ya hace Compras desde su orden de compra.
 *
 * Se carga en diferido y no con un import normal a propósito: `Ordenes`
 * importa de este archivo —usa `FichaTot` para enseñar los vales de la
 * orden— así que un import directo cerraría el círculo. Con `lazy` el
 * círculo no existe en el módulo, sólo al pulsar el folio, y de paso la
 * pantalla de vales no arrastra la de órdenes hasta que hace falta.
 */
const FichaDeLaOrden = lazy(
  () => import('./Ordenes').then((m) => ({ default: m.Ficha })),
);

/**
 * La tasa con la que se cierra el vale.
 *
 * Está aquí para verla mientras se captura; la que vale es la que guarda
 * la API con el vale, para que uno de hace dos años siga diciendo lo que
 * dijo aunque la ley cambie.
 */
const TASA_IVA = 16;

/**
 * TOT · Trabajos en otros talleres.
 *
 * El vale con el que APRISA autoriza pagarle a un tercero la mano de obra
 * o el trabajo de torno de una orden. Hoy se imprime y se firma en papel.
 *
 * **No es la cotización.** La cotización de la orden es todo lo que se le
 * va a cobrar al cliente —refacciones, mano de obra, trabajos de torno— y
 * de ahí sale la factura. El TOT es sólo lo que un tercero nos cobra a
 * nosotros: es gasto, y por eso no lleva precio de venta.
 *
 * Lo que sí lleva es un puente. Cada renglón se puede «llevar a la
 * cotización» con su costo ya puesto, dejando por capturar lo que se le
 * cobra al cliente. Sin ese puente los mismos conceptos se teclean dos
 * veces, y el día que difieran nadie sabrá cuál manda.
 */
export default function TotPagina() {
  const { puede } = useSesion();

  /**
   * Dos vistas: la consulta de siempre y los vales por requerir.
   *
   * La segunda es una bandeja de trabajo —hay algo que decidir y mandar—
   * y la primera es consulta. Mezclarlas en una lista con filtro habría
   * dejado la decisión escondida detrás de un desplegable.
   */
  const [vista, setVista] = useState<'vales' | 'por_requerir'>('vales');

  const [busqueda, setBusqueda] = useState('');
  /**
   * Sin filtro de entrada: ésta es la pantalla de consulta y del reporte.
   *
   * Lo que espera firma se busca en Operaciones, junto con las demás
   * autorizaciones. Filtrar aquí por «registrada» de entrada creaba un
   * segundo lugar donde mirar lo mismo, y dos bandejas para una decisión
   * son dos que revisar y una que se olvida.
   */
  const [estatus, setEstatus] = useState('');
  const [regionId, setRegionId] = useState('');
  const [estadoId, setEstadoId] = useState('');
  const [sucursalId, setSucursalId] = useState('');
  const [conCosto, setConCosto] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [numero, setNumero] = useState(1);

  const [pagina, setPagina] = useState<Pagina<Tot> | null>(null);
  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [estados, setEstados] = useState<Opcion[]>([]);
  const [sucursales, setSucursales] = useState<Opcion[]>([]);
  const [descargando, setDescargando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [abierto, setAbierto] = useState<Tot | null>(null);
  /** La orden abierta desde el folio de la lista, sin salir de aquí. */
  const [ordenDeLaLista, setOrdenDeLaLista] = useState<string | null>(null);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
  }, []);

  // Los estados se acotan a la zona elegida, y las sucursales al estado.
  // Son 40 estados y 365 sucursales: ofrecerlos todos cuando ya se filtró
  // arriba es pedirle a alguien que busque en una lista que ya sabemos
  // acortar.
  useEffect(() => {
    api.get<Opcion[]>('/catalogos/estados/opciones', { region_id: regionId || undefined })
      .then(setEstados)
      .catch(() => setEstados([]));
    setEstadoId('');
    setSucursalId('');
  }, [regionId]);

  useEffect(() => {
    api.get<Pagina<{ id: string; determinante: string; nombre: string }>>('/sucursales', {
      estadoId: estadoId || undefined,
      regionId: regionId || undefined,
      limite: 400,
      estado: 'activo',
    })
      .then((r) => setSucursales(
        r.data.map((s) => ({ id: s.id, nombre: `${s.determinante} — ${s.nombre}` })),
      ))
      .catch(() => setSucursales([]));
  }, [regionId, estadoId]);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Tot>>('/tot', {
      busqueda: busqueda || undefined,
      estatus: estatus || undefined,
      regionId: regionId || undefined,
      estadoId: estadoId || undefined,
      sucursalId: sucursalId || undefined,
      conCosto: conCosto || undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, estatus, regionId, estadoId, sucursalId, conCosto, desde, hasta, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  async function descargarReporte() {
    setError(null);
    setDescargando(true);
    try {
      await api.descargar('/tot/reporte', {
        busqueda: busqueda || undefined,
        estatus: estatus || undefined,
        regionId: regionId || undefined,
        estadoId: estadoId || undefined,
        sucursalId: sucursalId || undefined,
        conCosto: conCosto || undefined,
        desde: desde || undefined,
        hasta: hasta || undefined,
      }, 'gastos-tot.xlsx');
    } catch (e) {
      setError(e);
    } finally {
      setDescargando(false);
    }
  }

  // Lo que suma lo que se está viendo. No es el total del periodo —la
  // lista va paginada— y por eso se dice de esta página, no a secas.
  const sumaVisible = useMemo(
    () => (pagina?.data ?? [])
      .reduce((s, t) => s + t.total, 0),
    [pagina],
  );

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Trabajos en otros talleres</h1>
          <p className="sub">
            Lo que se le paga a un tercero por la mano de obra o el trabajo
            de torno de una orden. Lo que se captura es el costo: lo que a
            APRISA le cuesta. Lo que se le cobra al cliente va aparte, y en
            una iguala full no se cobra.
          </p>
        </div>
        <div className="controles" style={{ marginBottom: 0 }}>
          {/* El reporte respeta los filtros que están puestos: lo que se
              descarga es lo que se está viendo, en el periodo completo y
              no sólo la página. */}
          {puede('tot', 'exportar') && (
            <button disabled={descargando} onClick={descargarReporte}>
              {descargando ? 'Generando…' : 'Generar reporte de gastos'}
            </button>
          )}
          {/* El alta vive en la ficha de la orden, no aquí. Un vale
              cuelga siempre de una OT, y crearlo desde la orden quita el
              paso de buscarla —y de escoger la que no era—. Esta pantalla
              queda para consultar y para el reporte. */}
        </div>
      </div>

      <nav className="submenu">
        <button
          className={vista === 'vales' ? 'activo' : ''}
          onClick={() => setVista('vales')}
        >
          Vales
        </button>
        <button
          className={vista === 'por_requerir' ? 'activo' : ''}
          onClick={() => setVista('por_requerir')}
        >
          Por requerir pago
        </button>
      </nav>

      {vista === 'por_requerir' && <PorRequerirPago puede={puede} />}

      {vista === 'vales' && (
      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Folio, proveedor, orden o descripción…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
          <select value={estatus} onChange={(e) => { setEstatus(e.target.value); setNumero(1); }}>
            <option value="">Todos los estados</option>
            <option value="registrada">Por autorizar</option>
            <option value="autorizada">Autorizadas, por pagar</option>
            <option value="pagada">Pagadas</option>
            <option value="rechazada">Rechazadas</option>
            <option value="cancelada">No aplican para pago</option>
          </select>
          <select value={regionId} onChange={(e) => { setRegionId(e.target.value); setNumero(1); }}>
            <option value="">Todas las zonas</option>
            {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <select value={estadoId} onChange={(e) => { setEstadoId(e.target.value); setNumero(1); }}>
            <option value="">{regionId ? 'Todos los estados de la zona' : 'Todos los estados'}</option>
            {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
          <select
            value={sucursalId}
            onChange={(e) => { setSucursalId(e.target.value); setNumero(1); }}
          >
            <option value="">Todas las sucursales</option>
            {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
          <select value={conCosto} onChange={(e) => { setConCosto(e.target.value); setNumero(1); }}>
            <option value="">Con y sin cargo al cliente</option>
            <option value="si">Sólo con cargo</option>
            <option value="no">Sólo sin cargo (iguala full)</option>
          </select>
          <label className="campo" style={{ margin: 0 }}>
            <span className="ayuda">Desde</span>
            <input
              type="date" value={desde}
              onChange={(e) => { setDesde(e.target.value); setNumero(1); }}
            />
          </label>
          <label className="campo" style={{ margin: 0 }}>
            <span className="ayuda">Hasta</span>
            <input
              type="date" value={hasta}
              onChange={(e) => { setHasta(e.target.value); setNumero(1); }}
            />
          </label>
        </div>

        <Error error={error} />

        {cargando ? <Cargando /> : (
          <>
            {pagina?.data.length === 0 ? (
              <div className="vacio">No hay TOT que cumplan ese filtro.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Fecha</th>
                    <th>Orden</th>
                    <th>Sucursal</th>
                    <th>Proveedor</th>
                    <th className="numero">Total a pagar</th>
                    <th>Estatus</th>
                    <th>En TRIRIGA</th>
                  </tr>
                </thead>
                <tbody>
                  {pagina?.data.map((t) => (
                    <tr key={t.id}>
                      <td className="mono">
                        <button className="enlace" onClick={() => setAbierto(t)}>{t.folio}</button>
                      </td>
                      <td>{fechaCorta(t.fecha)}</td>
                      {/* Y el folio de la orden también abre la orden. Es
                          la pregunta que más se hace desde esta lista: de
                          qué trabajo es este gasto. */}
                      <td className="mono">
                        {t.orden ? (
                          <button className="enlace" onClick={() => setOrdenDeLaLista(t.orden!.id)}>
                            {folioOT(t.orden.folio)}
                          </button>
                        ) : '—'}
                      </td>
                      <td>
                        {t.orden?.sucursal
                          ? `${t.orden.sucursal.determinante} — ${t.orden.sucursal.nombre}`
                          : '—'}
                      </td>
                      <td>{t.proveedor?.nombre ?? '—'}</td>
                      <td className="numero">{dinero(t.total)}</td>
                      <td><EtiquetaEstatusTot valor={t.estatus} /></td>
                      <td>{t.orden?.estatusTririga ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {pagina && pagina.data.length > 0 && (
              <div className="totales" style={{ marginTop: 12 }}>
                <div>
                  En esta página <strong>{dinero(sumaVisible)}</strong>
                  {' '}<span className="ayuda">sin contar los cancelados</span>
                </div>
              </div>
            )}

            {pagina && (
              <Paginacion
                pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
                onCambiar={setNumero}
              />
            )}
          </>
        )}
      </div>
      )}

      {abierto && (
        <FichaTot
          tot={abierto}
          puede={puede}
          onCerrar={() => { setAbierto(null); cargar(); }}
        />
      )}

      {ordenDeLaLista && (
        <Suspense fallback={<Cargando />}>
          <FichaDeLaOrden
            id={ordenDeLaLista}
            tecnicos={[]}
            onCerrar={() => setOrdenDeLaLista(null)}
            onCambio={cargar}
          />
        </Suspense>
      )}
    </>
  );
}

/**
 * Los vales que esperan su requerimiento de pago agrupado.
 *
 * Karin: «todas las TOT de ordenes del tecnico Joel son pagadas una
 * semana despues y eso se le manda a contabilidad a pago, pero como un
 * bulto, es decir se juntaron 10 TOT se suman y se pagan en una
 * exhibicion».
 *
 * Un grupo es un taller y un técnico. El beneficiario es el taller —un
 * pago es una transferencia a una cuenta— y lo que agrupa es el
 * calendario del técnico. Dos talleres son dos requerimientos, y eso no
 * se puede negociar aquí: lo rechaza la API.
 *
 * Se puede destildar lo que no entre todavía, y mandar un solo vale
 * también vale: un grupo de uno es un grupo, y es el vale urgente de un
 * técnico agrupado sin necesidad de permiso ni excepción.
 */
function PorRequerirPago({ puede }: { puede: (m: string, a: string) => boolean }) {
  const [datos, setDatos] = useState<PorRequerir | null>(null);
  const [fuera, setFuera] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    api.get<PorRequerir>('/tot/por-requerir').then(setDatos).catch(setError);
  }, []);

  useEffect(cargar, [cargar]);

  const alternar = (id: string) => setFuera((antes) => {
    const copia = new Set(antes);
    if (copia.has(id)) copia.delete(id); else copia.add(id);
    return copia;
  });

  const dentro = (grupo: GrupoPorRequerir) =>
    grupo.vales.filter((v) => !fuera.has(v.id));

  // La suma de lo que de verdad va: destildar un vale tiene que mover la
  // cifra, porque es la cifra que se va a transferir.
  const suma = (grupo: GrupoPorRequerir) =>
    dentro(grupo).reduce((s, v) => s + v.total, 0);

  async function requerir(grupo: GrupoPorRequerir) {
    setError(null);
    setHecho(null);
    setTrabajando(grupo.clave);
    try {
      const r = await api.post<PorRequerir>('/tot/requerir', {
        valeIds: dentro(grupo).map((v) => v.id),
      });
      setDatos(r);
      setFuera(new Set());
      setHecho(`${grupo.proveedorNombre}: ${dentro(grupo).length} vale(s) mandados a pago.`);
    } catch (e) {
      setError(e);
      // Se recarga igual: el error normal aquí es que alguien más ya los
      // mandó, y entonces la lista de la pantalla está vieja.
      cargar();
    } finally {
      setTrabajando(null);
    }
  }

  return (
    <div className="panel">
      <p className="sub" style={{ marginTop: 0 }}>
        Vales ya autorizados de técnicos que cobran en grupo. Cada grupo es
        <strong> un taller y un técnico</strong>, y sale como un solo pago por
        la suma: contabilidad ve un renglón y transfiere una vez. Destilda lo
        que no entre todavía.
      </p>

      <Error error={error} />
      {hecho && <p className="etiqueta si">{hecho}</p>}

      {!datos ? <Cargando /> : datos.grupos.length === 0 ? (
        <div className="vacio">
          No hay vales esperando requerimiento. Aparecen aquí al autorizar un
          vale de una orden cuyo técnico cobra en grupo.
        </div>
      ) : datos.grupos.map((grupo) => (
        <div key={grupo.clave} className="panel" style={{ marginBottom: 12 }}>
          <div className="encabezado" style={{ marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0 }}>{grupo.proveedorNombre}</h3>
              <p className="sub" style={{ margin: 0 }}>
                Órdenes de {grupo.tecnicoNombre ?? 'un técnico sin asignar'}
                {' · '}{dentro(grupo).length} de {grupo.vales.length} vale(s)
              </p>
            </div>
            <div className="controles" style={{ marginBottom: 0 }}>
              <strong style={{ fontSize: 18 }}>{dinero(suma(grupo))}</strong>
              {puede('tot', 'ver') && (
                <button
                  className="primario"
                  disabled={trabajando !== null || dentro(grupo).length === 0}
                  onClick={() => requerir(grupo)}
                >
                  {trabajando === grupo.clave ? 'Mandando…' : 'Mandar a pago'}
                </button>
              )}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>Va</th>
                <th>TOT</th>
                <th>Orden</th>
                <th className="numero">Importe</th>
              </tr>
            </thead>
            <tbody>
              {grupo.vales.map((v) => (
                <tr key={v.id} style={fuera.has(v.id) ? { opacity: 0.45 } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!fuera.has(v.id)}
                      onChange={() => alternar(v.id)}
                    />
                  </td>
                  <td className="mono">{v.folio}</td>
                  <td className="mono">{folioOT(v.ordenFolio)}</td>
                  <td className="numero">{dinero(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════ presentación

export function dinero(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
}

const ETIQUETAS: Record<string, { texto: string; clase: string }> = {
  registrada: { texto: 'Por autorizar', clase: 'no' },
  autorizada: { texto: 'Autorizada, por pagar', clase: '' },
  pagada: { texto: 'Pagada', clase: 'si' },
  rechazada: { texto: 'Rechazada', clase: 'no' },
  /**
   * «No aplica para pago» y no «Cancelada» a secas: dice lo que pasó con
   * el dinero, que es lo que se está mirando en esta lista. El vale no
   * desapareció —el trabajo se hizo— y a nadie se le paga por él.
   */
  cancelada: { texto: 'No aplica para pago', clase: '' },
};

/** El estado del vale, con su color. Lo usa también la ficha de la orden. */
export function EtiquetaEstatusTot({ valor }: { valor: string }) {
  const e = ETIQUETAS[valor] ?? { texto: valor, clase: '' };
  return <span className={`etiqueta ${e.clase}`}>{e.texto}</span>;
}

// ═══════════════════════════════════════════════════════════════════ alta

interface RenglonEnCaptura {
  llave: number;
  cantidad: string;
  descripcion: string;
  costoUnitario: string;
}

let contador = 0;
function renglonVacio(): RenglonEnCaptura {
  contador += 1;
  return { llave: contador, cantidad: '1', descripcion: '', costoUnitario: '' };
}

function completo(r: RenglonEnCaptura): boolean {
  return r.descripcion.trim().length >= 3
    && Number(r.cantidad) > 0
    && r.costoUnitario !== '' && Number(r.costoUnitario) >= 0;
}

/**
 * El alta del vale.
 *
 * Se exporta porque el TOT se crea desde la orden: la ficha de la OT la
 * abre con `ordenFija` y entonces no hay nada que buscar. Antes había que
 * entrar aquí y encontrar la orden en un buscador, que es el paso al
 * revés y deja abierta la puerta a colgar el vale de la orden
 * equivocada.
 */
export function FormularioTot({
  onCerrar, onGuardado, ordenFija,
}: {
  onCerrar: () => void;
  onGuardado: () => void;
  /** Cuando viene de una orden, ya está decidida y no se busca. */
  ordenFija?: Orden;
}) {
  const [buscandoOrden, setBuscandoOrden] = useState('');
  const [ordenes, setOrdenes] = useState<Orden[]>([]);
  const [orden, setOrden] = useState<Orden | null>(ordenFija ?? null);

  const [proveedores, setProveedores] = useState<Opcion[]>([]);
  const [proveedorId, setProveedorId] = useState('');
  const [costoCliente, setCostoCliente] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [renglones, setRenglones] = useState<RenglonEnCaptura[]>([renglonVacio()]);

  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  /**
   * Por qué no hay proveedores que ofrecer, si es que no los hay.
   *
   * Antes esta petición fallaba en silencio —`.catch(() => undefined)`— y
   * el desplegable se quedaba vacío sin decir nada. Quien registraba el
   * TOT veía «Elige…» y ninguna opción, y no había forma de saber si el
   * padrón estaba vacío, si la sesión había caducado o si le faltaba el
   * permiso, que era el caso: supervisión y soporte operativo no tenían
   * `proveedores.ver`.
   *
   * Un hueco que no se explica se busca en el lugar equivocado.
   */
  const [porQueNoHay, setPorQueNoHay] = useState<string | null>(null);

  useEffect(() => {
    api.get<Pagina<{ id: string; nombre: string }>>('/proveedores', {
      limite: 200, estado: 'activo',
    })
      .then((r) => {
        setProveedores(r.data);
        setPorQueNoHay(r.data.length > 0 ? null
          : 'No hay proveedores activos dados de alta. El padrón lo administra Compras.');
      })
      .catch((e: unknown) => {
        setPorQueNoHay(e instanceof ErrorApi && e.esSinPermiso
          ? 'Tu usuario no tiene permiso para ver el padrón de proveedores, así que'
            + ' la lista no se puede cargar. Lo concede el administrador.'
          : 'No se pudo cargar el padrón de proveedores. Vuelve a abrir la pantalla;'
            + ' si sigue igual, avisa.');
      });
  }, []);

  // Las órdenes se buscan, no se listan: son miles. Se pide sólo cuando
  // hay algo escrito, para no traer la primera página por nada.
  useEffect(() => {
    if (orden || buscandoOrden.trim().length < 3) { setOrdenes([]); return; }

    const t = setTimeout(() => {
      api.get<Pagina<Orden>>('/ordenes', { busqueda: buscandoOrden.trim(), limite: 8 })
        .then((r) => setOrdenes(r.data))
        .catch(() => setOrdenes([]));
    }, 300);

    return () => clearTimeout(t);
  }, [buscandoOrden, orden]);

  // Los montos del vale son más IVA: se captura el subtotal y se
  // transfiere el total. Las cuentas de aquí son sólo para verlas
  // mientras se llena; las que valen las hace la API al guardar, con la
  // misma función, para que el vale no diga un total y el pago otro.
  const totales = useMemo(() => {
    const centavos = (n: number) => Math.round(n * 100) / 100;
    const subtotal = centavos(renglones.reduce(
      (s, r) => s + centavos((Number(r.cantidad) || 0) * (Number(r.costoUnitario) || 0)), 0,
    ));
    const iva = centavos(subtotal * (TASA_IVA / 100));

    return { subtotal, iva, total: centavos(subtotal + iva) };
  }, [renglones]);

  // La iguala de la sucursal decide si el trabajo se le cobra al cliente.
  // Nulo es que la orden no la tiene capturada: no se sabe, que no es lo
  // mismo que saber que no se cobra.
  const cobrable = orden?.tipoIguala ? orden.tipoIguala.lleva_costo : null;

  const listo = Boolean(orden) && Boolean(proveedorId) && renglones.every(completo);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/tot', {
        ordenTrabajoId: orden!.id,
        proveedorId,
        costoCliente: cobrable && costoCliente.trim() !== ''
          ? Number(costoCliente) : undefined,
        observaciones: observaciones.trim() || undefined,
        renglones: renglones.map((r) => ({
          cantidad: Number(r.cantidad),
          descripcion: r.descripcion.trim(),
          costoUnitario: Number(r.costoUnitario),
        })),
      });
      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  function cambiar(llave: number, campo: keyof RenglonEnCaptura, valor: string) {
    setRenglones((rs) => rs.map((r) => (r.llave === llave ? { ...r, [campo]: valor } : r)));
  }

  return (
    <Dialogo
      titulo="Nuevo TOT"
      ancho
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : 'Registrar TOT'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="orden">Orden de trabajo *</label>
        {orden ? (
          <div className="controles" style={{ marginBottom: 0 }}>
            <strong className="mono">{folioOT(orden.folio)}</strong>
            <span>
              {orden.sucursal
                ? `${orden.sucursal.determinante} — ${orden.sucursal.nombre}`
                : 'sin sucursal'}
            </span>
            {/* Viniendo de la orden no se cambia: el vale es de ésa, y
                dejar cambiarla aquí es reabrir la puerta que quisimos
                cerrar —colgar el gasto de la orden equivocada—. */}
            {!ordenFija && (
              <button onClick={() => { setOrden(null); setBuscandoOrden(''); }}>Cambiar</button>
            )}
          </div>
        ) : (
          <>
            <input
              id="orden" type="search" value={buscandoOrden}
              onChange={(e) => setBuscandoOrden(e.target.value)}
              placeholder="Folio de la OT, sucursal o determinante…"
            />
            {ordenes.length > 0 && (
              <div className="panel" style={{ marginTop: 6 }}>
                {ordenes.map((o) => (
                  <div key={o.id} className="controles" style={{ marginBottom: 6 }}>
                    <button className="enlace" onClick={() => setOrden(o)}>
                      {folioOT(o.folio)}
                    </button>
                    <span>
                      {o.sucursal ? `${o.sucursal.determinante} — ${o.sucursal.nombre}` : '—'}
                    </span>
                    {o.tipoIguala && (
                      <span className="etiqueta">
                        {o.tipoIguala.nombre}
                        {o.tipoIguala.lleva_costo ? '' : ' · sin cargo'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <span className="ayuda">
          Si hay correctiva va en ésa. Si no, en la de iguala: ahí APRISA
          paga y no cobra, porque la full lo absorbe.
        </span>
      </div>

      {/* Los datos de la orden, a la vista antes de capturar el vale.
          Registrarlo sin ver de qué equipo es obliga a abrir la orden en
          otra pestaña, y ahí es donde se captura sobre la que no era. */}
      {orden && (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="rejilla-datos">
            <Dato etiqueta="Orden" valor={folioOT(orden.folio)} />
            <Dato etiqueta="Orden de servicio" valor={orden.noOsCliente ?? null} />
            <Dato
              etiqueta="Sucursal"
              valor={orden.sucursal
                ? `${orden.sucursal.determinante} — ${orden.sucursal.nombre}`
                : null}
            />
            <Dato etiqueta="Zona" valor={orden.region?.nombre ?? null} />
            <Dato etiqueta="Iguala" valor={orden.tipoIguala?.nombre ?? null} />
            <Dato etiqueta="Equipo" valor={orden.tipoEquipo?.nombre ?? orden.equipoTexto} />
            <Dato etiqueta="Marca" valor={orden.marca?.nombre ?? null} />
            <Dato etiqueta="Modelo" valor={orden.modelo?.nombre ?? null} />
            <Dato etiqueta="Técnico" valor={orden.tecnico?.nombre_completo ?? null} />
          </div>
        </div>
      )}

      {orden && cobrable === false && (
        <div className="aviso">
          Esa orden es una iguala <strong>{orden.tipoIguala?.nombre}</strong>. El
          TOT se paga igual, pero el trabajo no se le cobra al cliente: la
          iguala lo absorbe.
        </div>
      )}

      {orden && cobrable === null && (
        <div className="aviso">
          Esa orden no tiene tipo de iguala capturado, así que no se sabe
          si el trabajo se le cobra al cliente. Se captura en la sucursal.
        </div>
      )}

      <div className="campo">
        <label htmlFor="proveedor">Se le paga a *</label>
        <select
          id="proveedor" value={proveedorId}
          onChange={(e) => setProveedorId(e.target.value)}
        >
          <option value="">Elige…</option>
          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        <span className="ayuda">
          Los técnicos que cobran por fuera están dados de alta aquí como
          proveedores. El técnico de la orden sale solo en el vale.
        </span>
        {/* Un desplegable vacío no dice nada por sí solo, y sin esto
            parecía que el padrón estaba vacío cuando lo que faltaba era
            el permiso. */}
        {porQueNoHay && (
          <span className="ayuda" style={{ color: '#b02a37' }}>{porQueNoHay}</span>
        )}
      </div>

      <h2>Conceptos</h2>

      {renglones.map((r, i) => (
        <div key={r.llave} className="panel" style={{ marginBottom: 10 }}>
          <div className="campo">
            <label htmlFor={`desc-${r.llave}`}>Descripción *</label>
            <input
              id={`desc-${r.llave}`} value={r.descripcion} maxLength={300}
              onChange={(e) => cambiar(r.llave, 'descripcion', e.target.value)}
              placeholder="Limpieza de sistema eléctrico y recableado"
            />
          </div>
          <div className="rejilla-campos">
            <div className="campo">
              <label htmlFor={`cant-${r.llave}`}>Cantidad *</label>
              <input
                id={`cant-${r.llave}`} type="number" min="0.01" step="0.01" value={r.cantidad}
                onChange={(e) => cambiar(r.llave, 'cantidad', e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor={`costo-${r.llave}`}>Costo unitario *</label>
              <input
                id={`costo-${r.llave}`} type="number" min="0" step="0.01" value={r.costoUnitario}
                onChange={(e) => cambiar(r.llave, 'costoUnitario', e.target.value)}
              />
            </div>
          </div>
          {renglones.length > 1 && (
            <button onClick={() => setRenglones((rs) => rs.filter((x) => x.llave !== r.llave))}>
              Quitar concepto {i + 1}
            </button>
          )}
        </div>
      ))}

      <button onClick={() => setRenglones((rs) => [...rs, renglonVacio()])}>
        Agregar concepto
      </button>

      <div className="totales" style={{ marginTop: 12 }}>
        <div>Subtotal <strong>{dinero(totales.subtotal)}</strong></div>
        <div>IVA {TASA_IVA}% <strong>{dinero(totales.iva)}</strong></div>
        {/* «Total a pagar» y no «Total» a secas: debajo va el costo al
            cliente, y dos cifras juntas sin decir de quién es cada una se
            leen al revés. Ésta es la que se le transfiere al proveedor. */}
        <div style={{ fontSize: 18 }}>
          Total a pagar <strong>{dinero(totales.total)}</strong>
        </div>
      </div>

      {/* Sólo cuando la iguala lo cobra. En una full el campo no aparece:
          un importe ahí sale en el reporte como recuperable sin serlo. */}
      {cobrable && (
        <div className="campo">
          <label htmlFor="costoCliente">Costo al cliente, sin IVA</label>
          <input
            id="costoCliente" type="number" min="0" step="0.01" value={costoCliente}
            onChange={(e) => setCostoCliente(e.target.value)}
            placeholder="Sin capturar"
          />
          <span className="ayuda">
            Lo de arriba es lo que nos cuesta; esto es lo que se le cobra.
            Se puede dejar vacío y capturarlo después: casi nunca se sabe
            al abrir el vale.
          </span>
        </div>
      )}

      <div className="campo">
        <label htmlFor="observaciones">Observaciones</label>
        <textarea
          id="observaciones" rows={2} value={observaciones} maxLength={1000}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>
    </Dialogo>
  );
}

// ═════════════════════════════════════════════════════════════════ ficha

export function FichaTot({
  tot, puede, onCerrar,
}: {
  tot: Tot;
  puede: (m: string, a: string) => boolean;
  /** Cierra la ficha y refresca la lista: el estatus pudo cambiar. */
  onCerrar: () => void;
}) {
  /**
   * Arranca con el renglón de la lista y se completa al abrir.
   *
   * La lista trae lo que cabe en una tabla; la ficha trae además los
   * comprobantes del pago y lo que se propone cobrar, que son consultas
   * aparte y no tendría sentido hacer por renglón. Enseñar primero lo que
   * ya se tiene evita el parpadeo de una ficha vacía, pero hay que ir por
   * el resto: sin esto, el comprobante no aparecía nunca —sólo después de
   * alguna acción, que es lo único que recargaba—.
   */
  const [vale, setVale] = useState(tot);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');
  /**
   * El motivo de «no aplica» va aparte del de rechazar.
   *
   * Son dos decisiones distintas sobre el mismo vale y pueden ocurrir una
   * después de la otra: rechazado, corregido y al final absorbido por la
   * iguala. Con un solo campo, el segundo diálogo abriría con el texto
   * del primero y se firmaría sin leerlo.
   */
  const [noAplicando, setNoAplicando] = useState(false);
  const [motivoIguala, setMotivoIguala] = useState('');
  /**
   * El plan de pago, que se decide al autorizar.
   *
   * Karin: «gerencia al momento de autorizar y previo a que le llegue a
   * contabilidad, es quien decide si se paga parcial o total, no es
   * contabilidad quien decide». Por eso autorizar abre un diálogo en vez
   * de firmar de una: después de aquí ya no hay dónde decidirlo.
   */
  const [autorizando, setAutorizando] = useState(false);
  const [anticipo, setAnticipo] = useState('');
  const [motivoAnticipo, setMotivoAnticipo] = useState('');
  /** La orden abierta desde el folio del vale, sin salir de aquí. */
  const [ordenAbierta, setOrdenAbierta] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    const fresco = await api.get<Tot>(`/tot/${tot.id}`);
    setVale(fresco);
  }, [tot.id]);

  // Al abrir, la ficha completa. Falla en silencio y se queda con lo de
  // la lista: es mejor una ficha a medias que una que no abre.
  useEffect(() => { void recargar().catch(() => undefined); }, [recargar]);

  async function hacer(accion: () => Promise<unknown>) {
    setError(null);
    setTrabajando(true);
    try {
      await accion();
      await recargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`TOT ${vale.folio}`}
      ancho
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Error error={error} />

      <div className="rejilla-datos">
        <Dato etiqueta="Estatus" valor={ETIQUETAS[vale.estatus]?.texto ?? vale.estatus} />
        <Dato etiqueta="Fecha" valor={fechaCorta(vale.fecha)} />
        <Dato etiqueta="Se le paga a" valor={vale.proveedor?.nombre ?? null} />
        {/* Las cifras salieron de aquí. Estaban entre la sucursal y el
            equipo, y el dinero repartido entre los datos de la orden no
            se lee: ahora va junto y abajo, en un bloque de dos lados. */}
        {/* El folio abre la orden completa, sin salir del vale. Quien
            autoriza un gasto casi siempre quiere ver de qué trabajo es, y
            copiar el folio para buscarlo en otra pantalla era el camino
            largo para la pregunta más frecuente. */}
        <Dato
          etiqueta="Orden"
          valor={vale.orden ? folioOT(vale.orden.folio) : null}
          alClicar={vale.orden ? () => setOrdenAbierta(vale.orden!.id) : undefined}
        />
        <Dato etiqueta="Orden de servicio" valor={vale.orden?.noOsCliente ?? null} />
        <Dato
          etiqueta="Sucursal"
          valor={vale.orden?.sucursal
            ? `${vale.orden.sucursal.determinante} — ${vale.orden.sucursal.nombre}`
            : null}
        />
        <Dato etiqueta="Iguala" valor={vale.orden?.iguala?.nombre ?? null} />
        {/* Lo que decidió la orden, no lo que hace la iguala en general.
            Sin esto, quien miraba el vale de una orden marcada con costo
            en una sucursal full sólo veía «full» y concluía lo contrario
            de lo que alguien había decidido. */}
        <Dato
          etiqueta="¿Genera costo adicional?"
          valor={vale.orden
            ? `${vale.orden.tieneCosto ? 'Sí' : 'No'}${
              vale.orden.costoDecidido ? '' : ' · nadie lo ha decidido'}`
            : null}
        />
        {/* El precio de venta de la orden entera. Quien autoriza este
            gasto está decidiendo si se paga, y saber si el trabajo ya se
            le cotizó al cliente —y en cuánto— es la mitad de esa
            decisión. */}
        <Dato
          etiqueta="Precio de venta de la orden"
          valor={vale.orden?.precioVenta === null || vale.orden?.precioVenta === undefined
            ? null
            : dinero(vale.orden.precioVenta)}
        />
        <Dato
          etiqueta="Equipo"
          valor={vale.orden?.tipoEquipo?.nombre ?? vale.orden?.equipoTexto ?? null}
        />
        <Dato etiqueta="Marca" valor={vale.orden?.marca?.nombre ?? null} />
        <Dato etiqueta="Modelo" valor={vale.orden?.modelo?.nombre ?? null} />
        <Dato etiqueta="Técnico" valor={vale.orden?.tecnico?.nombre_completo ?? null} />
        <Dato etiqueta="Zona" valor={vale.orden?.region?.nombre ?? null} />
        <Dato etiqueta="En TRIRIGA" valor={vale.orden?.estatusTririga ?? null} />
        {vale.observaciones && (
          <div className="ancho-total">
            <Dato etiqueta="Observaciones" valor={vale.observaciones} />
          </div>
        )}
        {vale.motivoCancelacion && (
          <div className="ancho-total">
            <Dato etiqueta="Por qué se canceló" valor={vale.motivoCancelacion} />
          </div>
        )}
      </div>

      <h2>Conceptos</h2>
      <table>
        <thead>
          <tr>
            <th className="numero">Cant.</th>
            <th>Descripción</th>
            {/* «Costo unitario» y no «Costo» a secas: con cantidad 1 las
                dos columnas dicen la misma cifra, y sin el «unitario» se
                leen como dos cosas distintas que deberían diferir. */}
            <th className="numero">Costo unitario</th>
            <th className="numero">Importe</th>
            <th className="acciones"></th>
          </tr>
        </thead>
        <tbody>
          {vale.renglones.map((r) => (
            <tr key={r.id}>
              <td className="numero">{r.cantidad}</td>
              <td>{r.descripcion}</td>
              <td className="numero">{dinero(r.costoUnitario)}</td>
              <td className="numero">{dinero(r.importe)}</td>
              <td className="acciones">
                {/* En una orden sin costo no se ofrece: el renglón
                    entraría con precio de venta en cero, porque la iguala
                    lo absorbe. El botón prometería un cobro que no va a
                    existir. */}
                {r.enCotizacion
                  ? <span className="etiqueta si">Ya se le cobra</span>
                  : vale.orden?.llevaCosto === false
                    ? <span className="etiqueta no">Lo cubre la iguala</span>
                    : puede('tot', 'editar') && (
                    <button
                      disabled={trabajando}
                      title="Este concepto se le paga al taller pero no se le cobra al cliente. Con esto pasa a la cotización de la orden."
                      onClick={() => void hacer(
                        () => api.post(`/tot/${vale.id}/renglones/${r.id}/a-cotizacion`),
                      )}
                    >
                      Cobrar al cliente
                    </button>
                    )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Qué hace ese botón, dicho donde está el botón.
          Se llamaba «Llevar a la cotización» y Karin preguntó dos veces
          qué hacía: el nombre contaba el mecanismo —copia un renglón a
          otra tabla— y no el motivo por el que alguien lo pulsaría. El
          nombre nuevo dice el resultado, y esto dice cuándo usarlo. */}
      <div className="aviso">
        <strong>¿Cuándo uso «Cobrar al cliente»?</strong> Cuando el taller
        hizo algo que <strong>no venía en la cotización</strong> —abrió el
        equipo y encontró otra falla, por ejemplo—. Ese concepto existe
        sólo aquí, así que se le paga al taller y no se le cobra a Walmart:
        la pérdida no se ve, porque cada cifra vive en una pantalla
        distinta. Con el botón pasa a la cotización de la orden con su
        costo ya puesto, y sólo queda capturar el precio de venta. Los
        conceptos que ya venían de la cotización salen marcados y sin
        botón: agregarlos otra vez sería cobrarle dos veces lo mismo.
        {vale.orden?.llevaCosto === false && (
          <>
            {' '}En <strong>esta</strong> orden no aplica: no se le cobra nada
            al cliente, así que no hay dónde pasar el concepto.{' '}
            {vale.orden.porQueLlevaCosto}
          </>
        )}
      </div>

      <ElDinero
        vale={vale} puede={puede} trabajando={trabajando}
        onGuardar={(monto) => { void hacer(
          () => api.post(`/tot/${vale.id}/costo-cliente`, { costoCliente: monto }),
        ); }}
      />

      {vale.motivoRechazo && (
        <div className="aviso error" style={{ marginTop: 12 }}>
          Rechazado: {vale.motivoRechazo}
        </div>
      )}

      {/* Operaciones decide si el gasto procede. Al autorizarlo nace la
          solicitud de pago para Contabilidad, ya con su firma: no vuelve
          a pedir autorización porque sería pedir dos veces lo mismo. */}
      {vale.estatus === 'registrada' && puede('tot', 'autorizar') && (
        <div className="controles" style={{ marginTop: 12 }}>
          {/* Autorizar abre el diálogo del plan de pago en vez de firmar
              de una. Karin: «gerencia al momento de autorizar y previo a
              que le llegue a contabilidad, es quien decide si se paga
              parcial o total, no es contabilidad quien decide». Es un
              clic más, y es el clic donde se toma la decisión: después
              del alta ya no hay dónde tomarla. */}
          <button
            className="primario" disabled={trabajando}
            onClick={() => setAutorizando(true)}
          >
            Autorizar el gasto
          </button>
          <button disabled={trabajando} onClick={() => setRechazando(true)}>
            Rechazar
          </button>
          {/* Karin: «hay servicios que no se paga, por que entran en la
              iguala». No es rechazar: no hay nada que corregir y el vale
              no vuelve. Por eso es un botón aparte y no un motivo dentro
              del rechazo, que dejaría lo resuelto en la misma bandeja que
              lo que sigue pendiente. */}
          <button disabled={trabajando} onClick={() => setNoAplicando(true)}>
            No aplica para pago
          </button>
        </div>
      )}

      {/* Y si no le toca a quien lo está mirando, se dice.
          Karin: «pide autorizarla, no encuentro donde se autoriza». No
          había dónde: el vale estaba por autorizar y la sección de
          botones simplemente no se pintaba, así que la pantalla parecía
          rota en vez de parecer ajena. Es el mismo defecto del
          desplegable vacío que no decía por qué estaba vacío. */}
      {vale.estatus === 'registrada' && !puede('tot', 'autorizar') && (
        <div className="aviso" style={{ marginTop: 12 }}>
          Este vale está <strong>por autorizar</strong>, y a ti no te toca
          firmarlo: el gasto lo aprueba Operaciones. Con tu perfil puedes
          verlo y consultarlo, pero no autorizarlo, rechazarlo ni marcarlo
          como no aplicable para pago.
        </div>
      )}

      {/* Casi siempre el rechazo es un dato mal capturado, no un trabajo
          que no se hizo: se corrige y vuelve a revisión en vez de
          capturar otro vale que deje al primero de ruido en la orden. */}
      {vale.estatus === 'rechazada' && puede('tot', 'editar') && (
        <div className="controles" style={{ marginTop: 12 }}>
          <button
            className="primario" disabled={trabajando}
            onClick={() => { void hacer(() => api.post(`/tot/${vale.id}/volver-a-registrar`)); }}
          >
            Volver a mandar a revisión
          </button>
        </div>
      )}

      {vale.estatus === 'autorizada' && (
        <div className="aviso" style={{ marginTop: 12 }}>
          Autorizado. El pago ya está en la bandeja de Contabilidad; el vale
          queda pagado cuando lo ejecuten.
        </div>
      )}

      {/* En qué va el pago del vale.

          Karin, después de autorizar el TOT 16 con anticipo: «si entro a
          la misma TOT no veo el registro que autorice el pago parcial que
          ya ve contabilidad». El vale enseñaba su estatus y nada del
          dinero, así que quien firmó el plan no podía comprobar que se
          hubiera guardado ni ver si el anticipo ya salió —y «Autorizada,
          por pagar» se lee igual con cero pesos transferidos que con
          quinientos—.

          Es el mismo dato que mira Contabilidad, leído desde aquí: dos
          consultas del mismo hecho acaban diciendo cosas distintas. */}
      {vale.pago && (
        <>
          <h2>El pago</h2>

          {/* Dentro de un bulto, el avance de abajo es del bulto y no de
              este vale: el dinero es uno y no se reparte por vale. Decirlo
              aquí evita que «abonado la mitad» se lea como que este vale
              va a medias. */}
          {vale.pago.folioRequerimiento && (
            <div className="aviso">
              Este vale se paga dentro del requerimiento{' '}
              <strong className="mono">{vale.pago.folioRequerimiento}</strong>, junto
              con {vale.pago.valesDelPago - 1} vale(s) más del mismo taller. Lo que
              sigue es el avance <strong>del requerimiento completo</strong>, no de
              este vale.
            </div>
          )}

          <div className="datos">
            <Dato etiqueta="Cómo va" valor={vale.pago.comoVa} />
            {vale.pago.anticipoAutorizado !== null && (
              <Dato
                etiqueta="Anticipo autorizado"
                valor={dinero(vale.pago.anticipoAutorizado)}
              />
            )}
            {!vale.pago.saldado && (
              <Dato etiqueta="Falta por pagar" valor={dinero(vale.pago.falta)} />
            )}
          </div>

          {vale.pago.abonos.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th className="numero">Monto</th>
                  <th>Referencia</th>
                  <th>Concepto</th>
                </tr>
              </thead>
              <tbody>
                {vale.pago.abonos.map((a) => (
                  <tr key={a.id}>
                    <td>{fechaCorta(String(a.fechaPago))}</td>
                    <td className="numero">{dinero(a.monto)}</td>
                    <td className="mono">{a.referencia}</td>
                    <td>{a.concepto ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Y qué falta para que se salde, dicho para quien autorizó.
              La liquidación no la habilita gerencia: ya la autorizó al
              firmar el vale entero. La ejecuta Contabilidad cuando el
              taller entregue, con el mismo botón de siempre. */}
          {!vale.pago.saldado && vale.pago.abonos.length > 0 && (
            <div className="aviso">
              Contabilidad ya transfirió {dinero(vale.pago.abonado)}. Los{' '}
              {dinero(vale.pago.falta)} que faltan los registra ella misma
              cuando el taller entregue: no hay que volver a autorizar nada,
              porque el vale se autorizó completo. El vale queda «pagada» y la
              orden puede cerrar hasta que se salde.
            </div>
          )}
        </>
      )}

      {/* Lo que prueba que al taller ya se le pagó.
          Karin: «cuando soporte consulta las TOT pagadas necesita poder
          ver el comprobante de pago que contabilidad sube». Lo sube
          Contabilidad y hasta ahora sólo se veía desde su pantalla, que es
          donde soporte no entra. */}
      {vale.comprobantes && vale.comprobantes.length > 0 && (
        <>
          <h2>Comprobante del pago</h2>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {vale.comprobantes.map((c) => (
              <li key={c.id}>
                {/* No es un enlace: el token vive sólo en memoria y no
                    viaja en una navegación, así que abrirlo así daba 401
                    y el comprobante parecía perdido. */}
                <button
                  className="enlace mono"
                  onClick={() => {
                    void api.descargar(
                      `/tot/${vale.id}/comprobantes/${c.id}`, undefined, c.nombre,
                    ).catch(setError);
                  }}
                >
                  {c.nombre}
                </button>
                <span className="ayuda"> · {fechaCorta(c.creadoEn)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Y si ya se pagó y no hay archivo, se dice: el hueco es real y
          quien lo mire tiene a quién preguntarle. */}
      {vale.estatus === 'pagada' && vale.comprobantes?.length === 0 && (
        <div className="aviso" style={{ marginTop: 12 }}>
          Pagado, pero Contabilidad todavía no ha subido el comprobante.
        </div>
      )}

      {/* Todavía se puede parar después de firmado: la iguala se nota a
          veces al revisar el mes, y mientras Contabilidad no pague el
          dinero no ha salido. Ejecutado el pago, el botón desaparece. */}
      {vale.estatus === 'autorizada' && puede('tot', 'autorizar') && (
        <div className="controles" style={{ marginTop: 12 }}>
          <button disabled={trabajando} onClick={() => setNoAplicando(true)}>
            No aplica para pago
          </button>
        </div>
      )}

      {vale.estatus === 'cancelada' && (
        <div className="aviso" style={{ marginTop: 12 }}>
          No aplica para pago: a nadie se le paga por este vale. El trabajo
          sigue registrado en la orden.
        </div>
      )}

      {/* Autorizar el gasto, y con él decidir cómo se paga.
          Karin: «gerencia al momento de autorizar y previo a que le
          llegue a contabilidad, es quien decide si se paga parcial o
          total, no es contabilidad quien decide». El anticipo se pacta
          con el taller antes de que empiece el trabajo, así que cuando el
          pago llega a contabilidad el trato ya existe: lo que queda es
          cumplirlo. Por eso la decisión vive aquí y no en la bandeja de
          pagos, donde antes la tomaba quien transfería. */}
      {autorizando && (
        <Dialogo
          titulo={`Autorizar el TOT ${vale.folio}`}
          onCerrar={() => setAutorizando(false)}
          pie={(
            <>
              <button onClick={() => setAutorizando(false)}>Cancelar</button>
              <button
                className="primario" disabled={trabajando}
                onClick={() => {
                  const monto = Number(anticipo);
                  void hacer(() => api.post(`/tot/${vale.id}/autorizar`,
                    anticipo.trim() && monto > 0
                      ? { anticipo: monto, motivoAnticipo: motivoAnticipo.trim() || undefined }
                      : {}));
                  setAutorizando(false);
                  setAnticipo('');
                  setMotivoAnticipo('');
                }}
              >
                Autorizar el gasto
              </button>
            </>
          )}
        >
          <p>
            Se autoriza el gasto de <strong>{dinero(vale.total)}</strong> y con
            eso nace la solicitud de pago para Contabilidad, ya con tu firma.
          </p>

          <p>
            Si pactaste un <strong>anticipo</strong> con el taller —lo normal es
            que lo pidan antes de empezar y el resto contra entrega—, escríbelo
            aquí. Déjalo vacío y el pago se ejecuta completo, que es el caso
            normal. Contabilidad cumple lo que decidas: no puede partir el pago
            por su cuenta ni cambiar el importe.
          </p>

          <label>
            Anticipo, si lo hay
            <input
              type="number" min="0" step="0.01" value={anticipo}
              placeholder="Vacío = se paga completo"
              onChange={(e) => setAnticipo(e.target.value)}
            />
          </label>

          {anticipo.trim() && Number(anticipo) > 0 && (
            <>
              <div className="aviso" style={{ marginTop: 8 }}>
                {Number(anticipo) >= vale.total ? (
                  <>
                    El anticipo tiene que ser menor que {dinero(vale.total)}. Si
                    se va a pagar todo de una vez, eso es un pago completo: deja
                    el campo vacío.
                  </>
                ) : (
                  <>
                    Contabilidad transferirá <strong>{dinero(Number(anticipo))}</strong>
                    {' '}ahora y <strong>{dinero(vale.total - Number(anticipo))}</strong>
                    {' '}al liquidar. El vale queda pagado sólo con la
                    liquidación, y hasta entonces la orden no cierra.
                  </>
                )}
              </div>

              <label style={{ marginTop: 8 }}>
                Por qué se pactó el anticipo
                <input
                  value={motivoAnticipo}
                  placeholder="El taller lo pide antes de empezar"
                  onChange={(e) => setMotivoAnticipo(e.target.value)}
                />
              </label>
            </>
          )}
        </Dialogo>
      )}

      {rechazando && (
        <DialogoMotivo
          titulo={`Rechazar el TOT ${vale.folio}`}
          descripcion="Di por qué no procede. Quien lo capturó necesita saber qué corregir, y podrá arreglarlo y volver a mandarlo."
          textoBoton="Rechazar"
          motivo={motivo}
          setMotivo={setMotivo}
          trabajando={trabajando}
          error={null}
          onCerrar={() => setRechazando(false)}
          onConfirmar={() => {
            void hacer(() => api.post(`/tot/${vale.id}/rechazar`, { motivo }));
            setRechazando(false);
          }}
        />
      )}

      {/* La orden completa, encima del vale. Al cerrarla se vuelve al
          vale, que es donde estaba la decisión a medias. */}
      {ordenAbierta && (
        <Suspense fallback={<Cargando />}>
          <FichaDeLaOrden
            id={ordenAbierta}
            tecnicos={[]}
            onCerrar={() => setOrdenAbierta(null)}
            onCambio={() => { void recargar(); }}
          />
        </Suspense>
      )}

      {noAplicando && (
        <DialogoMotivo
          titulo={`El TOT ${vale.folio} no aplica para pago`}
          descripcion="Di por qué no se paga —lo normal es que el servicio entre en la iguala—. A diferencia de rechazarlo, esto no vuelve: el vale queda cerrado y nadie cobra por él."
          textoBoton="No aplica para pago"
          motivo={motivoIguala}
          setMotivo={setMotivoIguala}
          trabajando={trabajando}
          error={null}
          onCerrar={() => setNoAplicando(false)}
          onConfirmar={() => {
            void hacer(
              () => api.post(`/tot/${vale.id}/no-aplica`, { motivo: motivoIguala }),
            );
            setNoAplicando(false);
          }}
        />
      )}
    </Dialogo>
  );
}

/**
 * El dinero del vale, con sus dos lados enfrentados.
 *
 * Antes estaba repartido: el subtotal y el total salían arriba, entre la
 * sucursal y el equipo, y el cobro al cliente hasta abajo del todo. Dos
 * cifras que sólo significan algo comparadas, separadas por media
 * pantalla. Es la misma pregunta que se hace al cuadrar el mes —cuánto de
 * este gasto se recupera— y no se podía contestar sin desplazarse.
 *
 * Izquierda lo que sale, derecha lo que entra. En el vale se captura el
 * costo: lo que el tercero nos cobra. Lo que se le cobra al cliente es lo
 * otro, y en una iguala full no existe.
 *
 * Del lado del cliente los tres estados se dicen distinto, y eso es el
 * punto. «Sin capturar» pide que alguien lo mire; cero es una decisión
 * tomada —se revisó y no se le cobra—; y en una full ni se pregunta.
 * Enseñar cero en los tres casos borraría la diferencia, que es la que
 * decide si el gasto se cuenta como recuperable.
 */
function ElDinero({ vale, puede, trabajando, onGuardar }: {
  vale: Tot;
  puede: (m: string, a: string) => boolean;
  trabajando: boolean;
  onGuardar: (monto: number | null) => void;
}) {
  const guardado = vale.costoCliente === null ? '' : String(vale.costoCliente);
  const sugerido = vale.costoClienteSugerido;

  /**
   * El campo arranca con lo guardado, y si no hay nada, con lo propuesto.
   *
   * Karin: «no traemos el precio venta automatico? o por que lo tengo que
   * capturar nuevamente». Prellenado y no guardado solo: sigue haciendo
   * falta pulsar Guardar, porque esto es lo que se le cobra a Walmart y
   * conviene que alguien lo mire. Lo que sobraba era teclear otra vez un
   * número que el sistema ya conoce.
   */
  const inicial = guardado !== '' ? guardado
    : (sugerido ? String(sugerido.monto) : '');
  const [monto, setMonto] = useState(inicial);

  // Al recargar el vale manda lo que quedó guardado.
  useEffect(() => { setMonto(inicial); }, [inicial]);

  /** Si lo que se ve es la propuesta y todavía nadie la ha confirmado. */
  const sinConfirmar = guardado === '' && sugerido !== null
    && monto === String(sugerido.monto);

  const cobra = vale.orden?.llevaCosto;
  const editable = cobra === true && puede('tot', 'editar');

  return (
    <div
      style={{
        display: 'grid', gap: 12, marginTop: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      }}
    >
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Lo que nos cuesta</h3>
        <p className="sub" style={{ marginTop: 0 }}>
          Lo que {vale.proveedor?.nombre ?? 'el tercero'} nos cobra por este
          trabajo. Es lo que se captura en los conceptos.
        </p>
        <div className="totales">
          <div>Subtotal <strong>{dinero(vale.subtotal)}</strong></div>
          <div>IVA {vale.tasaIva}% <strong>{dinero(vale.iva)}</strong></div>
          <div style={{ fontSize: 18 }}>
            Total a pagar <strong>{dinero(vale.total)}</strong>
          </div>
        </div>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Lo que le cobramos al cliente</h3>

        {/* El porqué lo escribe la API, con la misma regla que decidió el
            sí o el no. Cuando la pantalla lo redactaba por su cuenta
            —«la iguala full lo absorbe»— acababa contradiciendo a quien
            había marcado la orden como generadora de costo. */}
        {cobra === false && (
          <p className="sub" style={{ marginTop: 0 }}>
            Nada. {vale.orden?.porQueLlevaCosto} Este trabajo se paga y no
            se cobra, y por eso en el reporte de gastos su celda sale en
            blanco.
          </p>
        )}

        {cobra === null && (
          <p className="sub" style={{ marginTop: 0 }}>
            No se sabe. {vale.orden?.porQueLlevaCosto}
          </p>
        )}

        {cobra === true && !editable && (
          <div className="totales">
            <div style={{ fontSize: 18 }}>
              <strong>
                {vale.costoCliente === null ? 'Sin capturar' : dinero(vale.costoCliente)}
              </strong>
            </div>
          </div>
        )}

        {editable && (
          <div className="campo">
            <label htmlFor="cobro">Costo al cliente, sin IVA</label>
            <div className="controles" style={{ marginBottom: 0 }}>
              <input
                id="cobro" type="number" min="0" step="0.01" value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="Sin capturar"
              />
              <button
                className="primario" disabled={trabajando || monto.trim() === guardado}
                onClick={() => onGuardar(monto.trim() === '' ? null : Number(monto))}
              >
                {sinConfirmar ? 'Confirmar' : 'Guardar'}
              </button>
            </div>
            <span className="ayuda">
              {sinConfirmar && sugerido
                ? `Propuesto, todavía sin capturar. ${sugerido.porque} Cámbialo si`
                  + ' no es lo que se le va a cobrar, y pulsa Confirmar.'
                : (vale.costoCliente === null
                  ? 'Sin capturar. Vacío no es cero: cero dice que se revisó y no se'
                    + ' le cobra, y en el reporte se imprime.'
                  : `Se le cobran ${dinero(vale.costoCliente)}. Vaciar el campo lo`
                    + ' devuelve a «sin capturar».')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}


function Dato({ etiqueta, valor, alClicar }: {
  etiqueta: string;
  valor: string | null;
  /** Si se pasa, el valor se pinta como enlace y lleva a algún lado. */
  alClicar?: () => void;
}) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      {valor && alClicar
        ? <span><button className="enlace" onClick={alClicar}>{valor}</button></span>
        : <span>{valor || '—'}</span>}
    </div>
  );
}
